import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { supabase } from "@/lib/supabaseClient";
import LoadingState from "@/components/base/LoadingState";
import PreviewTab from "./components/PreviewTab";
import MessagesListTab from "./components/MessagesListTab";
import PDFPrintTab from "./components/PDFPrintTab";
import { getExpiryStatus, getExpiryBadge } from "@/lib/signatureBookExpiry";

/* ─── Types ─────────────────────────────────────────── */
interface TokenRow {
  tenant_id: string;
  client_id: string;
  status: string;
  expires_at: string | null;
}

interface SignatureBook {
  id: string;
  tenant_id: string;
  title: string;
  status: string;
  event_date: string | null;
  cover_photo_url: string | null;
  closing_message: string | null;
  activated_at: string | null;
  expires_at: string | null;
  public_slug: string | null;
}

interface Photo {
  id: string;
  photo_url: string;
  sort_order: number;
}

interface Entry {
  id: string;
  guest_name: string;
  message: string | null;
  emoji: string | null;
  pos_x: number;
  pos_y: number;
  signature_data: string | null;
  photo_url: string | null;
  photo_id: string | null;
  status: string;
  created_at: string;
}

type TabId = "preview" | "messages" | "pdf";

const TABS: { id: TabId; label: string; icon: string }[] = [
  { id: "preview",  label: "Preview",  icon: "ri-layout-masonry-line" },
  { id: "messages", label: "Mensajes", icon: "ri-message-3-line" },
  { id: "pdf",      label: "PDF",      icon: "ri-file-pdf-2-line" },
];

const ACCENT = "#C8A96E";

/* ─── Error screen ─────────────────────────────────── */
function ErrorScreen({ message }: { message: string }) {
  return (
    <div className="min-h-screen bg-[#f3f7fb] flex items-center justify-center px-6">
      <div className="text-center max-w-sm space-y-5">
        <div className="w-16 h-16 mx-auto flex items-center justify-center rounded-full bg-amber-100">
          <i className="ri-book-2-line text-3xl text-amber-500"></i>
        </div>
        <h2 className="text-slate-800 font-bold text-lg">Libro no disponible</h2>
        <p className="text-slate-400 text-sm leading-relaxed">{message}</p>
      </div>
    </div>
  );
}

/* ─── Main Page ─────────────────────────────────────── */
export default function PortalSignatureBookPage() {
  const { token: portalToken } = useParams<{ token: string }>();

  const [loading,   setLoading]   = useState(true);
  const [errorMsg,  setErrorMsg]  = useState<string | null>(null);
  const [book,      setBook]      = useState<SignatureBook | null>(null);
  const [photos,    setPhotos]    = useState<Photo[]>([]);
  const [entries,   setEntries]   = useState<Entry[]>([]);
  const [activeTab, setActiveTab] = useState<TabId>("preview");

  useEffect(() => {
    const load = async () => {
      if (!portalToken) { setErrorMsg("Token inválido."); setLoading(false); return; }

      // Verify token
      const { data: tok } = await supabase
        .from("client_access_tokens")
        .select("tenant_id, client_id, status, expires_at")
        .eq("token", portalToken)
        .maybeSingle();

      if (!tok) { setErrorMsg("Link de portal inválido."); setLoading(false); return; }
      if (tok.status !== "active") { setErrorMsg("Este portal fue desactivado."); setLoading(false); return; }
      if (tok.expires_at && new Date(tok.expires_at) < new Date()) { setErrorMsg("Este portal expiró."); setLoading(false); return; }

      const tokenRow = tok as TokenRow;

      // Find signature book strictly matching tenant_id + client_id
      const { data: bookData } = await supabase
        .from("signature_books")
        .select("id, tenant_id, title, status, event_date, cover_photo_url, closing_message, activated_at, expires_at, public_slug, client_id")
        .eq("tenant_id", tokenRow.tenant_id)
        .eq("client_id",  tokenRow.client_id)
        .order("created_at", { ascending: false })
        .limit(1)
        .maybeSingle();

      if (!bookData) {
        setErrorMsg("No tienes un libro de firmas asignado. Contacta a tu fotógrafo.");
        setLoading(false);
        return;
      }

      // Security: double-check client ownership
      if ((bookData as { client_id: string }).client_id !== tokenRow.client_id) {
        setErrorMsg("Acceso denegado.");
        setLoading(false);
        return;
      }

      const b = bookData as SignatureBook;
      setBook(b);

      // Load photos and entries
      const [photoRes, entryRes] = await Promise.all([
        supabase
          .from("signature_book_photos")
          .select("id, photo_url, sort_order")
          .eq("signature_book_id", b.id)
          .order("sort_order"),
        supabase
          .from("signature_book_entries")
          .select("id, guest_name, message, emoji, pos_x, pos_y, signature_data, photo_url, photo_id, status, created_at")
          .eq("signature_book_id", b.id)
          .eq("status", "visible")
          .order("created_at", { ascending: false }),
      ]);

      setPhotos((photoRes.data as Photo[]) ?? []);
      setEntries((entryRes.data as Entry[]) ?? []);
      setLoading(false);
    };
    load();
  }, [portalToken]);

  if (loading) return <LoadingState fullPage text="Cargando tu libro de firmas…" />;
  if (errorMsg) return <ErrorScreen message={errorMsg} />;
  if (!book) return <ErrorScreen message="Error inesperado." />;

  const visibleEntries = entries.filter((e) => e.status === "visible");
  const sigCount        = visibleEntries.filter((e) => e.signature_data).length;
  const expStatus       = getExpiryStatus(book.expires_at);
  const expiryBadge     = getExpiryBadge(expStatus);
  const isExpired       = expStatus === "expired";

  return (
    <>
      <link rel="preconnect" href="https://fonts.googleapis.com" />
      <link href="https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,400;0,700;1,400&display=swap" rel="stylesheet" />

      <div className="min-h-screen bg-[#f3f7fb] print:bg-white">

        {/* Header — screen only */}
        <header className="bg-white border-b border-slate-200 sticky top-0 z-20 print:hidden">
          <div className="max-w-2xl mx-auto px-4 md:px-6 py-3 flex items-center gap-3">
            <Link
              to={`/portal/${portalToken}`}
              className="flex items-center gap-1.5 text-slate-400 hover:text-slate-600 text-sm transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-arrow-left-line"></i>
              Portal
            </Link>
            <span className="text-slate-200">/</span>
            <div className="flex items-center gap-2 min-w-0">
              <div
                className="w-7 h-7 flex items-center justify-center rounded-lg shrink-0"
                style={{ backgroundColor: ACCENT + "20" }}
              >
                <i className="ri-book-open-line text-sm" style={{ color: ACCENT }}></i>
              </div>
              <p className="text-slate-700 font-semibold text-sm truncate">{book.title}</p>
            </div>
            {/* Status + expiry badges */}
            <div className="ml-auto shrink-0 flex items-center gap-1.5">
              {book.status === "published" && !isExpired ? (
                <span className="text-[11px] font-semibold px-2.5 py-1 rounded-full bg-emerald-100 text-emerald-700">
                  Publicado
                </span>
              ) : book.status !== "published" ? (
                <span className="text-[11px] font-semibold px-2.5 py-1 rounded-full bg-slate-100 text-slate-500">
                  Borrador
                </span>
              ) : null}
              {expiryBadge.label && (
                <span className={`text-[11px] font-semibold px-2.5 py-1 rounded-full flex items-center gap-1 ${expiryBadge.className}`}>
                  <span className="w-3 h-3 flex items-center justify-center">
                    <i className={`${expiryBadge.icon} text-[10px]`}></i>
                  </span>
                  {expiryBadge.label}
                </span>
              )}
            </div>
          </div>
        </header>

        <div className="max-w-2xl mx-auto px-4 md:px-6 py-5 print:p-0">

          {/* Book info card — screen only */}
          <div
            className="rounded-2xl overflow-hidden mb-4 print:hidden"
            style={{ backgroundColor: "#1C1008" }}
          >
            <div className="relative h-28 overflow-hidden">
              {book.cover_photo_url ? (
                <>
                  <img
                    src={book.cover_photo_url}
                    alt=""
                    className="w-full h-full object-cover object-top"
                  />
                  <div className="absolute inset-0 bg-black/50" />
                </>
              ) : (
                <div
                  className="absolute inset-0"
                  style={{ background: "linear-gradient(135deg, #2C1A08, #1C1008)" }}
                />
              )}
              <div className="absolute inset-0 flex items-center px-5 gap-4">
                <div
                  className="w-12 h-12 flex items-center justify-center rounded-xl shrink-0"
                  style={{ backgroundColor: ACCENT + "30", border: `1.5px solid ${ACCENT}50` }}
                >
                  <i className="ri-book-open-line text-2xl" style={{ color: ACCENT }}></i>
                </div>
                <div className="min-w-0">
                  <h1
                    className="text-white font-bold text-base leading-tight truncate"
                    style={{ fontFamily: "'Playfair Display', Georgia, serif" }}
                  >
                    {book.title}
                  </h1>
                  {book.event_date && (
                    <p className="text-white/60 text-xs mt-0.5">
                      {new Date(book.event_date).toLocaleDateString("es-MX", {
                        day: "numeric", month: "long", year: "numeric",
                      })}
                    </p>
                  )}
                </div>
              </div>
            </div>
            {/* Stats row */}
            <div className="grid grid-cols-3 divide-x divide-white/10 px-0 py-3">
              {[
                { icon: "ri-image-2-line",   value: photos.length,         label: "Fotos" },
                { icon: "ri-message-3-line", value: visibleEntries.length, label: "Mensajes" },
                { icon: "ri-quill-pen-line", value: sigCount,              label: "Firmas" },
              ].map((stat) => (
                <div key={stat.label} className="flex flex-col items-center py-1 px-3">
                  <span className="text-lg font-bold" style={{ color: ACCENT }}>{stat.value}</span>
                  <span className="text-[10px] text-white/40 font-medium">{stat.label}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Expired banner */}
          {isExpired && (
            <div className="flex items-start gap-3 p-4 rounded-2xl mb-4 bg-red-50 border border-red-200 print:hidden">
              <span className="w-8 h-8 flex items-center justify-center rounded-lg bg-red-100 text-red-600 shrink-0">
                <i className="ri-close-circle-line text-base"></i>
              </span>
              <div>
                <p className="text-sm font-semibold text-red-700">Libro cerrado</p>
                <p className="text-xs text-red-500 mt-0.5">
                  El período de mensajes finalizó el{" "}
                  <strong>{new Date(book.expires_at!).toLocaleDateString("es-MX", { day: "numeric", month: "long", year: "numeric" })}</strong>.
                  Puedes seguir viendo y descargando el contenido.
                </p>
              </div>
            </div>
          )}

          {/* Soon-to-expire warning */}
          {expStatus === "soon" && !isExpired && (
            <div className="flex items-start gap-3 p-4 rounded-2xl mb-4 bg-amber-50 border border-amber-200 print:hidden">
              <span className="w-8 h-8 flex items-center justify-center rounded-lg bg-amber-100 text-amber-600 shrink-0">
                <i className="ri-time-line text-base"></i>
              </span>
              <div>
                <p className="text-sm font-semibold text-amber-700">Libro a punto de cerrar</p>
                <p className="text-xs text-amber-600 mt-0.5">
                  El libro aceptará mensajes hasta el{" "}
                  <strong>{new Date(book.expires_at!).toLocaleDateString("es-MX", { day: "numeric", month: "long", year: "numeric" })}</strong>.
                </p>
              </div>
            </div>
          )}

          {/* Guest book link — if active */}
          {book.status === "published" && book.public_slug && !isExpired && (
            <div
              className="flex items-center gap-3 p-4 rounded-2xl mb-4 print:hidden"
              style={{ backgroundColor: ACCENT + "10", border: `1px solid ${ACCENT}30` }}
            >
              <i className="ri-qr-code-line text-xl" style={{ color: ACCENT }}></i>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-slate-700">Libro de visitas activo</p>
                <p className="text-xs text-slate-400 mt-0.5 truncate">
                  {window.location.origin}/guestbook/{book.public_slug}
                </p>
              </div>
              <a
                href={`/guestbook/${book.public_slug}`}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-1.5 text-xs font-semibold px-3 py-2 rounded-xl cursor-pointer whitespace-nowrap transition-all"
                style={{ backgroundColor: ACCENT, color: "#fff" }}
              >
                <i className="ri-external-link-line"></i>
                Ver
              </a>
            </div>
          )}

          {/* Tabs — screen only */}
          <div className="flex items-center gap-1 bg-white rounded-2xl border border-slate-200 p-1.5 mb-4 print:hidden">
            {TABS.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-xl text-sm font-semibold transition-all cursor-pointer whitespace-nowrap ${
                  activeTab === tab.id
                    ? "text-white shadow-sm"
                    : "text-slate-400 hover:text-slate-600"
                }`}
                style={activeTab === tab.id ? { backgroundColor: ACCENT } : {}}
              >
                <span className="w-4 h-4 flex items-center justify-center">
                  <i className={`${tab.icon} text-sm`}></i>
                </span>
                {tab.label}
                {tab.id === "messages" && visibleEntries.length > 0 && (
                  <span
                    className="text-[10px] font-bold px-1.5 py-0.5 rounded-full"
                    style={
                      activeTab === "messages"
                        ? { backgroundColor: "rgba(255,255,255,0.3)", color: "#fff" }
                        : { backgroundColor: ACCENT + "20", color: ACCENT }
                    }
                  >
                    {visibleEntries.length}
                  </span>
                )}
              </button>
            ))}
          </div>

          {/* Tab content — screen only */}
          <div className="print:hidden">
            {activeTab === "preview" && (
              <PreviewTab
                photos={photos}
                entries={entries}
                coverPhotoUrl={book.cover_photo_url}
              />
            )}
            {activeTab === "messages" && (
              <MessagesListTab entries={entries} />
            )}
            {activeTab === "pdf" && (
              <PDFPrintTab book={book} photos={photos} entries={entries} />
            )}
          </div>
        </div>

        {/* Print content — only shows on print */}
        <div className="hidden print:block">
          <PDFPrintTab book={book} photos={photos} entries={entries} />
        </div>
      </div>
    </>
  );
}